import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import api from "../../../api";
import { Bar, Line } from "react-chartjs-2";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
} from "chart.js";
import {
  PieChart,
  CreditCard,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  BarChart3,
  LineChart,
  ChartColumn,
  ChartSpline,
} from "lucide-react";
import {
  FaCreditCard,
  FaCashRegister,
  FaCalendarCheck,
  FaClock,
  FaDollarSign,
  FaMoneyBillWave,
} from "react-icons/fa";
import Txt from "../../Text";
import textTranslator from "../../../utils/useTextTranslator";
import { arClass } from "../../../utils/useArClass";
import DateInput from "../../form/DateInput";
import CustomSelect from "../../CustomSelect";

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement
);

// StatCard Component (updated to match new design)
const StatCard = ({ icon, title, value, subtitle, trend, darkMode, color }) => {
  const colorClasses = {
    blue: darkMode
      ? "from-blue-900/20 to-blue-800/20 text-blue-400"
      : "from-blue-50 to-blue-100 text-blue-600",
    green: darkMode
      ? "from-emerald-900/20 to-emerald-800/20 text-emerald-400"
      : "from-emerald-50 to-emerald-100 text-emerald-600",
    purple: darkMode
      ? "from-purple-900/20 to-purple-800/20 text-purple-400"
      : "from-purple-50 to-purple-100 text-purple-600",
    amber: darkMode
      ? "from-amber-900/20 to-amber-800/20 text-amber-400"
      : "from-amber-50 to-amber-100 text-amber-600",
    red: darkMode
      ? "from-red-900/20 to-red-800/20 text-red-400"
      : "from-red-50 to-red-100 text-red-600",
  };

  return (
    <motion.div
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className={`p-5 rounded-xl border backdrop-blur-sm ${
        darkMode
          ? "bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700"
          : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
      }`}
    >
      <div className={arClass("flex items-start justify-between")}>
        <div>
          <div className={arClass(`inline-flex p-3 rounded-xl ${colorClasses[color]}`)}>
            {icon}
          </div>
          <h3
            className={arClass(`mt-4 text-sm max-sm:text-xs font-medium ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`)}
          >
            {title}
          </h3>
          <p
            className={arClass(`mt-2 text-2xl max-sm:text-xl font-bold ${
              darkMode ? "text-white" : "text-gray-900"
            }`)}
          >
            {typeof value === "number" ? Number(value).toLocaleString() : value}
          </p>
          <p
            className={arClass(`mt-1 text-sm max-sm:text-xs ${
              darkMode ? "text-gray-500" : "text-gray-500"
            }`)}
          >
            {subtitle}
          </p>
        </div>
        {trend && (
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium ${
              trend.value >= 0
                ? darkMode
                  ? "bg-emerald-900/30 text-emerald-400"
                  : "bg-emerald-100 text-emerald-700"
                : darkMode
                ? "bg-red-900/30 text-red-400"
                : "bg-red-100 text-red-700"
            }`}
          >
            {trend.value >= 0 ? (
              <TrendingUp size={14} />
            ) : (
              <TrendingDown size={14} />
            )}
            <span>{Math.abs(Number(trend.value || 0)).toFixed(1)}%</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ChartCard Component (updated to match new design)
const ChartCard = ({
  title,
  description,
  icon,
  children,
  darkMode,
  height = "h-72",
}) => {
  return (
    <div
      className={arClass(
        `p-5 md:p-6 rounded-xl border ${
          darkMode
            ? "bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700"
            : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
        }`
      )}
    >
      <div className={arClass("flex items-center justify-between mb-6")}>
        <div>
          <div className={arClass("flex items-center gap-3 mb-2")}>
            <div
              className={arClass(
                `p-2 rounded-lg ${
                  darkMode
                    ? "bg-blue-900/30 text-blue-400"
                    : "bg-blue-100 text-blue-600"
                }`
              )}
            >
              {icon}
            </div>
            <h3
              className={arClass(
                `text-lg max-sm:text-sm font-semibold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`
              )}
            >
              {title}
            </h3>
          </div>
          <p
            className={arClass(
              `text-sm max-sm:text-xs ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`
            )}
          >
            {description}
          </p>
        </div>
      </div>
      <div className={height}>{children}</div>
    </div>
  );
};

const Text = ({ children, className }) => {
  return (
    <Txt page="STATISTICS" className={className} keyName="STATISTICS">
      {children}
    </Txt>
  );
};

export default function PaymentStatistics() {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const [mode, setMode] = useState("month");
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(
    today.toISOString().slice(0, 10)
  );
  const [years] = useState(() => {
    const y = today.getFullYear();
    return Array.from({ length: 6 }).map((_, i) => y - (5 - i));
  });
  let yearsOptions = () => years.map((y) => ({ value: y, label: y.toString() }));
  let monthsOptions = () =>
    Array.from({ length: 12 }).map((_, i) => {
      const m = i + 1;
      return { value: m, label: new Date(0, i).toLocaleString(defaultLanguage, { month: "long" }) };
    });

  const [monthlyChartType, setMonthlyChartType] = useState("bar");
  const [loading, setLoading] = useState(false);
  const [serverData, setServerData] = useState(null);
  const [stats, setStats] = useState({
    totalPayments: 0,
    completedPayments: 0,
    totalAmount: 0,
    avgAmount: 0,
    methods: {},
    timeSlots: {},
    monthly: {},
    paymentsTrend: {},
    monthlyTrend: 0,
  });

  // Chart colors and gradients
  const chartColors = useMemo(
    () => [
      "#8B5CF6",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#3B82F6",
      "#EC4899",
      "#14B8A6",
      "#F97316",
      "#6366F1",
      "#84CC16",
    ],
    []
  );

  const gradientColors = useMemo(
    () => [
      ["#8B5CF6", "#7C3AED"],
      ["#10B981", "#059669"],
      ["#F59E0B", "#D97706"],
      ["#EF4444", "#DC2626"],
      ["#3B82F6", "#2563EB"],
    ],
    []
  );

  const createGradient = (ctx, color1, color2) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
    return gradient;
  };

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const params = { mode };
        if (mode === "year") params.year = selectedYear;
        if (mode === "month") {
          params.year = selectedYear;
          params.month = selectedMonth;
        }
        if (mode === "day") params.date = selectedDate;

        const res = await api.get("/statistics/payments", { params });
        const d = res?.data ?? null;
        setServerData(d);

        const mapped = {
          totalPayments: d?.metrics?.totalPayments ?? d?.total ?? 0,
          completedPayments: d?.metrics?.completedPayments ?? 0,
          totalAmount: d?.metrics?.totalAmount ?? 0,
          avgAmount: d?.metrics?.avgAmount ?? 0,
          methods: d?.metrics?.methods ?? {},
          timeSlots: (d?.metrics?.timeSlotDistribution || []).reduce(
            (acc, cur) => {
              const hour = Number(cur.hour);
              if (hour >= 6 && hour < 12)
                acc.morning = (acc.morning || 0) + cur.count;
              else if (hour >= 12 && hour < 18)
                acc.afternoon = (acc.afternoon || 0) + cur.count;
              else if (hour >= 18 && hour < 22)
                acc.evening = (acc.evening || 0) + cur.count;
              else acc.night = (acc.night || 0) + cur.count;
              return acc;
            },
            {}
          ),
          monthly: {},
          paymentsTrend: d?.metrics?.paymentsTrend ?? {},
          monthlyTrend: d?.metrics?.paymentsTrend?.changePercent ?? 0,
        };

        if (d?.labels && d?.series && Array.isArray(d.series) && d.series[0]) {
          const labels = d.labels;
          const dataArr = d.series[0].data || [];
          const monthlyObj = {};
          labels.forEach((lab, idx) => {
            monthlyObj[lab] = dataArr[idx] ?? 0;
          });
          mapped.monthly = monthlyObj;
        }

        setStats(mapped);
      } catch (err) {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [mode, selectedYear, selectedMonth, selectedDate]);

  const methodChartData = useMemo(() => {

    const translateMethod = (method) => {
      switch (method) {
        case "cash" || " Cash":
          return textTranslator("cash", "PAYMENTS");
        case "credit-card":
          return textTranslator("credit-card", "PAYMENTS");
        case "debit-card":
          return textTranslator("debit-card", "PAYMENTS");
        case "insurance":
          return textTranslator("insurance", "PAYMENTS");
        case "online":
          return textTranslator("online", "PAYMENTS");
        case "online-payment":
          return textTranslator("online-payment", "PAYMENTS");
        case "other":
          return textTranslator("other", "PAYMENTS");
        default:
          return method;
      }
    };

    const ctx = document.createElement("canvas").getContext("2d");
    const rawLabels = Object.keys(stats.methods || {});
    const labels = rawLabels.map((l) => translateMethod(l));
    const data = rawLabels.map((k) => stats.methods[k] || 0);
    const bgColors = rawLabels.map((_, i) => {
      const g = gradientColors[i % gradientColors.length];
      return createGradient(ctx, g[0], g[1]);
    });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: bgColors,
          borderWidth: 2,
          borderColor: darkMode ? "rgb(31, 41, 55)" : "white",
          hoverBorderWidth: 3,
          hoverBorderColor: darkMode ? "rgb(55, 65, 81)" : "rgb(243, 244, 246)",
          borderRadius: 12,
          spacing: 6,
        },
      ],
    };
  }, [stats.methods, darkMode, gradientColors]);

  // Separate datasets for bar and line (trend) so the trend can render without background fill
  const monthlyBarData = useMemo(() => {
    const ctx = document.createElement("canvas").getContext("2d");
    const labels = serverData?.labels?.length
      ? serverData.labels
      : Object.keys(stats.monthly || {});
    const translatedLabels = labels.map((l) => textTranslator(l, 'STATISTICS') || l);
    const data = serverData?.series?.[0]?.data?.length
      ? serverData.series[0].data
      : labels.map((l) => stats.monthly?.[l] || 0);
    return {
      labels: translatedLabels,
      datasets: [
        {
          label: textTranslator("payments", "STATISTICS"),
          data,
          backgroundColor: createGradient(ctx, "#8B5CF6", "#7C3AED"),
          borderColor: "#8B5CF6",
          borderWidth: 2,
          borderRadius: 8,
        },
      ],
    };
  }, [stats.monthly, serverData]);

  const monthlyLineData = useMemo(() => {
    const labels = serverData?.labels?.length
      ? serverData.labels
      : Object.keys(stats.monthly || {});
    const translatedLabels = labels.map((l) => textTranslator(l, 'STATISTICS') || l);
    const data = serverData?.series?.[0]?.data?.length
      ? serverData.series[0].data
      : labels.map((l) => stats.monthly?.[l] || 0);
    return {
      labels: translatedLabels,
      datasets: [
        {
          label: textTranslator("payments", "STATISTICS"),
          data,
          borderColor: "#8B5CF6",
          borderWidth: 2.5,
          pointRadius: 3,
          pointHoverRadius: 6,
          tension: 0.35,
          fill: false,
          backgroundColor: "transparent",
        },
      ],
    };
  }, [stats.monthly, serverData]);

  // Payment trend datasets
  const paymentTrends = useMemo(() => {
    const labels = serverData?.labels?.length
      ? serverData.labels
      : Object.keys(stats.monthly || {});
    const payments = serverData?.series?.[0]?.data?.length
      ? serverData.series[0].data
      : labels.map((l) => stats.monthly?.[l] || 0);
    // If the server doesn't provide a counts series, derive a sensible fallback:
    // - if payments contains numeric entries, treat any positive payment as 1 (presence count)
    // - otherwise, fall back to zeros
    const counts = serverData?.series?.[1]?.data?.length
      ? serverData.series[1].data
      : payments.map((p) => (Number(p) ? Number(p) : 0));

    const rolling = (arr, windowSize = 3) => {
      const out = [];
      for (let i = 0; i < arr.length; i++) {
        const slice = arr.slice(Math.max(0, i - (windowSize - 1)), i + 1);
        const sum = slice.reduce((s, x) => s + (Number(x) || 0), 0);
        out.push(Number((sum / slice.length).toFixed(2)));
      }
      return out;
    };

    const rollingPayments = rolling(payments, 3);

    const ctx = document.createElement("canvas").getContext("2d");

    const lineOpts = {
      labels,
      datasets: [
        {
          label: textTranslator('amountLine','STATISTICS'),
          data: payments,
          borderColor: "#8B5CF6",
          backgroundColor: createGradient(
            ctx,
            "rgba(139,92,246,0.2)",
            "rgba(139,92,246,0.05)"
          ),
          tension: 0.4,
          pointRadius: 3,
          pointBorderWidth: 2,
          pointBackgroundColor: darkMode ? "#1F2937" : "#FFFFFF",
          pointBorderColor: "#8B5CF6",
          fill: true,
        },
      ],
    };

    const barOpts = {
      labels,
      datasets: [
        {
          label: textTranslator('countsBar','STATISTICS'),
          data: counts,
          backgroundColor: createGradient(ctx, "#10B981", "#059669"),
          borderWidth: 0,
          borderRadius: 6,
        },
      ],
    };

    const areaOpts = {
      labels,
      datasets: [
        {
          label: textTranslator('rollingAvgArea','STATISTICS'),
          data: rollingPayments,
          borderColor: "#3B82F6",
          backgroundColor: createGradient(
            ctx,
            "rgba(59,130,246,0.2)",
            "rgba(59,130,246,0.05)"
          ),
          tension: 0.3,
          fill: true,
          pointRadius: 3,
          pointBorderWidth: 2,
          pointBackgroundColor: darkMode ? "#1F2937" : "#FFFFFF",
          pointBorderColor: "#3B82F6",
        },
      ],
    };

    return { lineOpts, barOpts, areaOpts };
  }, [serverData, stats.monthly, darkMode]);

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: "right",
        labels: {
          color: darkMode ? "#E5E7EB" : "#4B5563",
          font: { family: "Inter, sans-serif", size: 12 },
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: darkMode
          ? "rgba(17, 24, 39, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
        titleColor: darkMode ? "#F3F4F6" : "#111827",
        bodyColor: darkMode ? "#D1D5DB" : "#374151",
        borderColor: darkMode ? "#374151" : "#E5E7EB",
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.raw || 0;
            return `${label}: ${
              typeof value === "number" ? value.toLocaleString() : value
            }`;
          },
        },
      },
    },
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 1000,
      easing: "easeOutQuart",
    },
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: darkMode ? "rgb(31, 41, 55)" : "white",
      },
    },
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: darkMode
            ? "rgba(55, 65, 81, 0.3)"
            : "rgba(229, 231, 235, 0.5)",
          drawBorder: false,
        },
        ticks: {
          color: darkMode ? "#9CA3AF" : "#6B7280",
          font: { family: "Inter, sans-serif" },
          align: defaultLanguage === 'ar' ? 'end' : 'start'
        },
        position: defaultLanguage === 'ar' ? 'right' : 'left'
      },
      x: {
        grid: { display: false },
        ticks: {
          color: darkMode ? "#9CA3AF" : "#6B7280",
          font: { family: "Inter, sans-serif" },
        },
        reverse: defaultLanguage === 'ar'
      },
    },
    plugins: {
      ...chartOptions.plugins,
      legend: { display: false },
    },
  };

  const miniOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: darkMode
          ? "rgba(17, 24, 39, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
        titleColor: darkMode ? "#F3F4F6" : "#111827",
        bodyColor: darkMode ? "#D1D5DB" : "#374151",
        borderColor: darkMode ? "#374151" : "#E5E7EB",
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
      },
    },
    scales: {
      x: { display: false },
      y: { display: false },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        radius: 0,
        hoverRadius: 4,
      },
    },
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={arClass(`flex items-center justify-center p-8 rounded-2xl h-[600px] ${
          darkMode
            ? "bg-gradient-to-br from-gray-800 to-gray-900"
            : "bg-gradient-to-br from-gray-50 to-white"
        }`)}
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            className={arClass(`w-16 h-16 border-4 rounded-full border-t-transparent mx-auto ${
              darkMode ? "border-blue-500" : "border-blue-600"
            }`)}
          />
          <p className={arClass(`mt-4 ${darkMode ? "text-gray-400" : "text-gray-600"}`)}>
            <Text>loadingPaymentsAnalytics</Text>
          </p> 
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={arClass(`relative p-4 sm:p-6 rounded-2xl ${
        darkMode
          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
          : "bg-gradient-to-br from-gray-50 via-white to-gray-50"
      }  border ${darkMode ? "border-gray-800" : "border-gray-200"}`)}
    >
      {/* Header */}
      <div className={arClass("flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8")}>
        <div className={arClass("flex items-center gap-4")}>
          <div
            className={arClass(`p-3 rounded-xl ${
              darkMode
                ? "bg-gradient-to-br from-blue-900/30 to-indigo-900/30 text-blue-400"
                : "bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600"
            }`)}
          >
            <CreditCard size={24} />
          </div>
          <div>
            <h1
              className={arClass(
                `text-2xl max-sm:text-lg font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`
              )}
            >
              <Text>paymentsAnalytics</Text>
            </h1>
            <p
              className={arClass(
                `text-sm max-sm:text-xs ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                } mt-1`
              )}
            >
              <Text>paymentsOverview</Text>
            </p>
          </div>
        </div>

        <div className={arClass("flex items-center gap-3 max-sm:flex-col max-sm:items-stretch max-sm:w-full")}>
          <div
            className={arClass(
              "flex items-center gap-2 max-sm:w-full max-sm:justify-between"
            )}
          >
            {["day", "month", "year"].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={arClass(
                  `p-3 text-sm rounded-md font-medium transition-colors border w-full sm:w-auto max-sm:text-xs max-sm:p-2 ${
                    mode === m
                      ? "bg-blue-600 text-white"
                      : darkMode
                      ? "bg-gray-700 border-gray-300 text-white hover:bg-gray-900"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`
                )}
              >
                {textTranslator(m, "STATISTICS")}
              </button>
            ))}
          </div>

          {/* Dynamic selectors based on mode */}
          <div
            className={arClass(
              `flex items-center gap-2 max-sm:w-full max-sm:justify-between`
            )}
          >
            {mode === "year" && (
              <CustomSelect
                    options={yearsOptions()}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    allowCreate={false}
                    allowSearch={false}
                    darkMode={darkMode}
                    className="min-w-[150px] max-sm:min-w-max"
                  />
            
            )}

            {mode === "month" && (
              <>
                <CustomSelect
                    options={yearsOptions()}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    allowCreate={false}
                    allowSearch={false}
                    darkMode={darkMode}
                    className="min-w-[150px] max-sm:min-w-max"
                  />

                <CustomSelect
                  options={monthsOptions()}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  allowCreate={false}
                  allowSearch={false}
                  darkMode={darkMode}
                  className="min-w-[150px] max-sm:min-w-max"
                />
              </>
            )}

            {mode === "day" && (
              <DateInput
                value={selectedDate}
                onChange={(e) => {
                  if (
                    selectedDate !== e.target.value &&
                    e.target.value !== ""
                  ) {
                    setSelectedDate(e.target.value);
                    const parts = e.target.value.split("-");
                    if (parts.length === 3) {
                      setSelectedYear(Number(parts[0]));
                      setSelectedMonth(Number(parts[1]));
                    }
                  }
                }}
                name="date-input" // Add a name for form handling
                className={`p-2 rounded-md border text-sm w-full max-sm:text-xs min-w-[300px] max-sm:min-w-max ${
                  darkMode
                    ? "bg-gray-700 text-white border-gray-300 hover:bg-gray-900"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
                placeholder={
                  defaultLanguage === "en"
                    ? "Select date"
                    : defaultLanguage === "ar"
                    ? "اختر تاريخًا"
                    : "Sélectionner une date"
                }
                // Optional: Add minDate and maxDate constraints
                // minDate="2024-01-01"
                // maxDate="2024-12-31"
                format="YYYY-MM-DD" // Match your current date format
                showTodayButton={true}
                allowClear={true}
                // If you're using React Hook Form, pass these props:
                // register={register}
                // registerProp="dateFieldName"
              />
            )}
          </div>
        </div>
      </div>

      {stats.totalPayments === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={arClass(`p-12 rounded-xl text-center ${
            darkMode ? "bg-gray-800/50" : "bg-gray-50"
          }`)}
        >
          <div
            className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
              darkMode
                ? "bg-gray-800 text-gray-400"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            <CreditCard size={32} />
          </div>
          <h3
            className={arClass(
              `text-xl font-semibold mb-2 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`
            )}
          >
            <Text>noPaymentDataTitle</Text>
          </h3>
          <p
            className={arClass(
              `mb-6 ${darkMode ? "text-gray-400" : "text-gray-500"}`
            )}
          >
            <Text>noPaymentDataMessage</Text>
          </p>
          <button
            className={arClass(
              `px-6 py-2.5 rounded-lg font-medium ${
                darkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              } transition-colors`
            )}
          >
            <Text>processFirstPayment</Text>
          </button>
        </motion.div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={<Calendar size={20} />}
              title={textTranslator("totalPayments", "STATISTICS")}
              value={stats.totalPayments}
              subtitle={textTranslator("overview", "STATISTICS")}
              trend={{ value: stats.monthlyTrend }}
              darkMode={darkMode}
              color="blue"
            />

            <StatCard
              icon={<FaCalendarCheck size={20} />}
              title={textTranslator("completedPayments", "STATISTICS")}
              value={stats.completedPayments}
              subtitle={`${(
                (stats.completedPayments / stats.totalPayments) *
                100
              ).toFixed(1)}% ${textTranslator("completionRate", "STATISTICS")}`}
              darkMode={darkMode}
              color="green"
            />

            <StatCard
              icon={<DollarSign size={20} />}
              title={textTranslator("totalAmount", "STATISTICS")}
              value={stats.totalAmount}
              subtitle={textTranslator("fromVisits", "STATISTICS")}
              darkMode={darkMode}
              color="purple"
            />

            <StatCard
              icon={<DollarSign size={20} />}
              title={textTranslator("avgAmount", "STATISTICS")}
              value={Number(stats.avgAmount).toFixed(2)}
              subtitle={textTranslator("of_total", "STATISTICS")}
              darkMode={darkMode}
              color="amber"
            />
          </div>

          {/* Main Charts: split into two columns - left doughnut, right breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Left column: Doughnut chart */}
            <ChartCard
              title={textTranslator("paymentMethods", "STATISTICS")}
              description={textTranslator(
                "paymentMethodsDescription",
                "STATISTICS"
              )}
              icon={<FaCreditCard size={20} />}
              darkMode={darkMode}
              height="h-[420px] md:h-[580px]"
            >
              <div className="h-full flex items-center justify-center">
                <div className="w-full max-w-3xl h-[460px] flex items-center justify-center">
                  <Doughnut data={methodChartData} options={chartOptions} />
                </div>
              </div>
            </ChartCard>

            {/* Right column: Methods breakdown */}
            <ChartCard
              title={textTranslator("methodsBreakdown", "STATISTICS")}
              description={textTranslator("methodsBreakdownDesc", "STATISTICS")}
              icon={<FaCreditCard size={20} />}
              darkMode={darkMode}
              height="h-[420px] md:h-[580px]"
            >
              <div className="h-full overflow-y-auto p-2">
                <div className="flex flex-col gap-3">
                  {methodChartData.labels.map((label, index) => {
                    const value =
                      methodChartData.datasets?.[0]?.data?.[index] ?? 0;
                    const percentage =
                      stats.totalPayments > 0
                        ? (value / stats.totalPayments) * 100
                        : 0;
                    const pctText = percentage.toFixed(1);
                    const color =
                      gradientColors[index % gradientColors.length]?.[0] ||
                      chartColors[index % chartColors.length];
                    return (
                      <div
                        key={label}
                        className={arClass(`p-3 rounded-lg border ${
                          darkMode
                            ? "bg-gray-800/30 border-gray-700"
                            : "bg-white border-gray-100"
                        }`)}
                      >
                        <div className={arClass("flex items-center justify-between mb-2")}>
                          <div
                            className={arClass(`text-sm max-sm:text-xs font-medium ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`)}
                          >
                            {label}
                          </div>
                          <div
                            className={`text-sm max-sm:text-xs font-bold ${
                              darkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {Number(value || 0).toLocaleString()}
                          </div>
                        </div>
                        <div
                          className={`h-2 rounded-full overflow-hidden ${
                            darkMode ? "bg-gray-700" : "bg-gray-100"
                          }`}
                        >
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 1, delay: index * 0.1 }}
                            style={{ background: color }}
                            className="h-full rounded-full"
                          />
                        </div>
                        <div className="text-xs max-sm:text-[11px] mt-2 text-right text-gray-500">
                          {pctText}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Middle Row: Payment Trends */}
          <ChartCard
            title={textTranslator("paymentTrends", "STATISTICS")}
            description={textTranslator("paymentTrends", "STATISTICS")}
            icon={<TrendingUp size={20} />}
            darkMode={darkMode}
            height="h-full"
          >
            <div className="h-full grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                className={`p-4 rounded-lg border flex flex-col ${
                  darkMode
                    ? "bg-gray-800/30 border-gray-700"
                    : "bg-white border-gray-100"
                }`}
              >
                <div
                  className={arClass(`text-sm mb-2 ${
                    darkMode ? "text-gray-400" : "text-gray-600"
                  }`)}
                >
                  {textTranslator('amountLine','STATISTICS')}
                </div> 
                <div className="flex-1">
                  <Line data={paymentTrends.lineOpts} options={miniOptions} />
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border flex flex-col ${
                  darkMode
                    ? "bg-gray-800/30 border-gray-700"
                    : "bg-white border-gray-100"
                }`}
              >
                <div
                  className={arClass(`text-sm mb-2 ${
                    darkMode ? "text-gray-400" : "text-gray-600"
                  }`)}
                >
                  {textTranslator('countsBar','STATISTICS')}
                </div> 
                <div className="flex-1">
                  <Bar data={paymentTrends.barOpts} options={miniOptions} />
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border flex flex-col ${
                  darkMode
                    ? "bg-gray-800/30 border-gray-700"
                    : "bg-white border-gray-100"
                }`}
              >
                <div
                  className={arClass(`text-sm mb-2 ${
                    darkMode ? "text-gray-400" : "text-gray-600"
                  }`)}
                >
                  {textTranslator('rollingAvgArea','STATISTICS')}
                </div> 
                <div className="flex-1">
                  <Line data={paymentTrends.areaOpts} options={miniOptions} />
                </div>
              </div>
            </div>
          </ChartCard>

          {/* Bottom Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <ChartCard
              title={textTranslator("monthlyPayments", "STATISTICS")}
              description={textTranslator("paymentTrends", "STATISTICS")}
              icon={<BarChart3 size={20} />}
              darkMode={darkMode}
              height="h-64 md:h-72"
            >
              <div className="w-full h-full flex flex-col">
                <div className={arClass("flex items-center space-x-2 mb-2")}>
                                  <div
                                    className={arClass(
                                      "flex  rounded-lg p-1 gap-1 border border-gray-100"
                                    )}
                                  >
                                    <button
                                      onClick={() => setMonthlyChartType("bar")}
                                      className={arClass(
                                        `p-2 text-sm rounded-md transition-colors  ${
                                          darkMode
                                            ? "bg-slate-600/80 hover:text-white"
                                            : "bg-slate-200 hover:text-black"
                                        } ${
                                          monthlyChartType === "bar" && darkMode
                                            ? "!bg-green-600/80 !text-green-200 border-green-200"
                                            : monthlyChartType === "bar"
                                            ? "!bg-green-200 !text-green-900"
                                            : "text-gray-600  hover:text-gray-900 "
                                        }`
                                      )}
                                    >
                                      <ChartColumn size={20} className="inline-block ml-1" />
                                    </button>
                                    <button
                                      onClick={() => setMonthlyChartType("line")}
                                      className={arClass(
                                        `p-2 text-sm rounded-md transition-colors  ${
                                          darkMode
                                            ? "bg-slate-600/80 hover:text-white"
                                            : "bg-slate-200 hover:text-black"
                                        } ${
                                          monthlyChartType === "line" && darkMode
                                            ? "!bg-green-600/80 !text-green-200 border-green-200"
                                            : monthlyChartType === "line"
                                            ? "!bg-green-200 !text-green-900"
                                            : "text-gray-600  hover:text-gray-900 "
                                        }`
                                      )}
                                    >
                                      <ChartSpline size={20} className="inline-block " />
                                    </button>
                                  </div>
                  </div>

                <div className="flex-1">
                  {monthlyChartType === "bar" ? (
                    <div className="h-full">
                      <Bar data={monthlyBarData} options={barChartOptions} />
                    </div>
                  ) : (
                    <div className="h-full">
                      <Line
                        data={monthlyLineData}
                        options={{
                          ...barChartOptions,
                          elements: {
                            line: {
                              tension: 0.4,
                            },
                            point: {
                              radius: 3,
                              hoverRadius: 6,
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </ChartCard>

            <ChartCard
              title={textTranslator('timeSlotDistribution','STATISTICS')}
              description={textTranslator('preferredVisitTimes','STATISTICS')}
              icon={<FaClock size={20} />}
              darkMode={darkMode}
              height="h-56 md:h-64"
            >
              <div className="h-full flex flex-col justify-center">
                <div className="space-y-4">
                  {Object.entries(stats.timeSlots).map(
                    ([slot, count], index) => {
                      const percentage = (
                        (count / stats.totalPayments) *
                        100
                      ).toFixed(1);
                      const Icon = FaClock;

                      return (
                        <div key={slot} className={arClass("flex items-center gap-4")}>
                          <div
                            className={arClass(`p-2 rounded-lg ${
                              darkMode ? "bg-gray-800/60" : "bg-gray-100"
                            }`)}
                          >
                            <Icon
                              size={16}
                              className={
                                darkMode ? "text-gray-400" : "text-gray-600"
                              }
                            />
                          </div>
                          <div className={arClass("flex-1")}>
                            <div className={arClass("flex justify-between mb-1")}>
                              <span
                                className={arClass(`font-medium capitalize ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`)}
                              >
                                {textTranslator(slot, 'STATISTICS')}
                              </span>
                              <span
                                className={`font-bold ${
                                  darkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {count}
                              </span>
                            </div>
                            <div
                              className={`h-2 rounded-full overflow-hidden ${
                                darkMode ? "bg-gray-700" : "bg-gray-200"
                              }`}
                            >
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 1, delay: index * 0.1 }}
                                className="h-full rounded-full"
                                style={{
                                  backgroundColor:
                                    chartColors[index % chartColors.length],
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Footer Summary */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className={arClass(`mt-8 p-4 rounded-xl border ${
              darkMode
                ? "bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-gray-700"
                : "bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-blue-200"
            }`)}
          >
            <div className={arClass("flex items-center justify-between")}>
              <div className="flex items-center gap-3">
                <div
                  className={arClass(`p-2 rounded-lg ${
                    darkMode
                      ? "bg-blue-900/30 text-blue-400"
                      : "bg-blue-100 text-blue-600"
                  }`)}
                >
                  <TrendingUp size={18} />
                </div>
                <div>
                  <p
                    className={arClass(`text-sm font-medium ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`)}
                  >
                    <Text>paymentSummary</Text>
                  </p> 
                  <p
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {stats.totalPayments.toLocaleString()}{" "}
                    {textTranslator(
                      "totalPayments",
                      "STATISTICS"
                    ).toLowerCase()}{" "}
                    • {stats.completedPayments.toLocaleString()}{" "}
                    {textTranslator(
                      "completedPayments",
                      "STATISTICS"
                    ).toLowerCase()}{" "}
                    • ${stats.totalAmount.toLocaleString()}{" "}
                    {textTranslator("totalAmount", "STATISTICS").toLowerCase()}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
